import DropDownSelectInput from "components/DropdownSelectInput";
import SearchComponent from "./Search";
import { Fragment, useEffect, useState } from "react";
import Chip from "./Chip";
import DownArrow from "assets/images/svg/down-arrow.svg";
import DataTable from "react-data-table-component";
import Skeleton from "react-loading-skeleton";
import DatepickerField from "components/DatePickerField";

const skeletonModal = (totalItems) => {
    const elements = [];
    for (let index = 0; index < totalItems; index++) {
        elements[index] = (
            <div className={`flex align-center w-100 p-3 ${index === totalItems - 1 ? "" : "border-bottom-1"}`}>
                <div className="w-35 ml-6">
                    <Skeleton />
                </div>
                <div className="w-20 ml-4">
                    <Skeleton />
                </div>
                <div className="w-20 ml-4">
                    <Skeleton />
                </div>
            </div>
        );
    }
    return <div className="w-100">{elements}</div>;
};

const SubHeaderComponent = ({
    data,
    filteredItems,
    onSearchAndFilter,
    searchFilter = {},
    selectFilters = [],
    isServer,
    theme,
    tableClassName,
    customStyle,
    filterProductColumn,
    selectedProducts,
    isShowSelectedProductData = false,
    isShowCheckboxInTable = false,
    progressPending,
}) => {
    const [selectedFilterValues, setSelectedFilterValues] = useState();
    const generateDependencies = () => {
        return selectFilters.map((item) => item?.value) || [];
    };

    /**
     * @function onLocalSearch
     * @description This function performs a local search and filtering operation on a dataset (data) based on the provided filterText and selectedFilterValues.
     * @param {string} filterText - The text to search for in the data.
     * @returns {Array} The filtered and searched results.
     */
    const onLocalSearch = (filterText) => {
        // Step 1: Filter the data based on the filterText and the specified searchBy fields
        const results = data?.filter((item) => {
            // Check if any field in the specified fields array (searchFilter.searchBy) contains the input filterText
            return searchFilter?.searchBy?.some((field) => {
                // Get the value of the nested field specified by the "field" string (e.g., "user.name")
                const fieldHierarchy = field.split(".");
                const value = fieldHierarchy.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : undefined), item);

                if (value !== undefined) {
                    return typeof value === "string" && value.toLowerCase().includes(filterText?.toLowerCase());
                }
                return false;
            });
        });

        // Step 2: Apply additional filtering based on the selectedFilterValues and selectFilters
        const filterMatch =
            selectedFilterValues?.length > 0
                ? results?.filter((item) => {
                      let isItemIncludesAllField = true;

                      // Iterate over each selectFilter
                      for (let index = 0; index < selectFilters.length; index++) {
                          const element = selectFilters[index];
                          const fieldHierarchy = element?.filterBy?.split(".");
                          const value = fieldHierarchy.reduce(
                              (obj, key) => (obj && obj[key] !== undefined ? obj[key] : undefined),
                              item,
                          );

                          // Check if the item matches the current filter criteria
                          if (isItemIncludesAllField) {
                              if (Array.isArray(element?.value) && element?.value?.length > 0) {
                                  isItemIncludesAllField = !!element?.value?.find((innerItem) => {
                                      if (Array.isArray(value)) {
                                          return value?.includes(innerItem?.value);
                                      } else {
                                          return innerItem?.value === value;
                                      }
                                  });
                              } else if (typeof element?.value === "object" && Object?.keys(element?.value)?.length > 0) {
                                  isItemIncludesAllField = element?.value?.value === value;
                              }
                          }
                      }
                      return isItemIncludesAllField;
                  })
                : results;

        // Step 3: Call the onSearchAndFilter function with the filtered and searched results
        return onSearchAndFilter?.(filterMatch);
    };

    useEffect(() => {
        setSelectedFilterValues([].concat(...selectFilters.map((item) => item?.value)?.filter(Boolean)));
    }, [...generateDependencies()]);

    useEffect(() => {
        !isServer && onLocalSearch(searchFilter?.value);
    }, [JSON.stringify(filteredItems), JSON.stringify(data), searchFilter?.value, selectedFilterValues, isServer]);

    const onRemoveSelectedFilter = (value, fieldType) => {
        // remove selected values for select
        selectFilters.forEach((filterType) => {
            const selectedValues =
                Array.isArray(filterType?.value) && filterType?.value?.length > 0
                    ? [...filterType?.value]
                    : filterType?.value
                      ? [filterType?.value]
                      : [];
            const currentIndex = fieldType
                ? selectedValues.findIndex((item) => item.value === value && item.fieldType === fieldType)
                : selectedValues.findIndex((item) => item.value === value);

            if (selectedValues.length > 0 && currentIndex > -1) {
                const updatedValues = selectedValues.filter((_, index) => index !== currentIndex);
                filterType?.onChange(updatedValues?.length > 0 ? updatedValues : "");
            }
        });
        const newSelectedFilters = selectedFilterValues.length > 0 ? [...selectedFilterValues] : [];
        const currentIndex = newSelectedFilters.findIndex((item) => item.value === value);
        newSelectedFilters?.splice(currentIndex, 1);
        setSelectedFilterValues(newSelectedFilters);
    };

    const onClearAllFilters = () => {
        setSelectedFilterValues();

        selectFilters.forEach((filterType) => {
            filterType?.onChange([]);
        });
    };

    const allfiltersOptions = selectFilters
        .map((item) => item.options)
        .flat()
        ?.filter(Boolean);

    return (
        <div>
            <div className="custom-table-header d-flex justify-between mb-3 gap-16 align-start">
                {searchFilter?.isShow && (
                    <SearchComponent
                        className={searchFilter?.className ? searchFilter?.className : "w-100"}
                        inputClassName="w-100"
                        onFilter={(value) => {
                            searchFilter?.onChange(value);
                        }}
                        onClear={() => {
                            searchFilter?.onChange("");
                        }}
                        filterText={searchFilter?.value}
                    />
                )}
                <div className="select-filter-container d-flex gap-16 flex-wrap justify-end mw-fit">
                    {selectFilters?.length > 0 &&
                        selectFilters?.map((item, index) => {
                            return (
                                <Fragment key={index}>
                                    {item?.type === "dateRange" ? (
                                        <DatepickerField
                                            className="mb-0 table-date-range-input"
                                            placeholder={item?.label}
                                            name={item?.label}
                                            onChange={(update) => {
                                                item?.onChange(update);
                                            }}
                                            startDate={item?.startDate}
                                            endDate={item?.endDate}
                                            peekNextMonth
                                            // showMonthDropdown
                                            locale={item?.locale}
                                            // showYearDropdown
                                            // dropdownMode="select"
                                            selectsRange
                                            datePickerClassName="id-datepicker-input"
                                            isClearable
                                            maxDate={new Date()}
                                        />
                                    ) : (
                                        <DropDownSelectInput
                                            key={index}
                                            className={`mb-0 ${item.dropdownContainerClassName}`}
                                            name={item?.label}
                                            options={(Array.isArray(item?.options) && item?.options) || []}
                                            label={
                                                <div className="d-flex">
                                                    {item?.label} <DownArrow width="16px" style={{ marginLeft: "6px" }} />
                                                </div>
                                            }
                                            value={item?.value}
                                            isSearchable
                                            isMulti={item?.isMulti}
                                            isClearable={false}
                                            onChange={(value) => {
                                                item?.onChange(value);
                                                // onSelect(value);
                                            }}
                                            isMultiSelectWithCheckbox={
                                                item?.isMultiSelectWithCheckbox !== undefined
                                                    ? item?.isMultiSelectWithCheckbox
                                                    : true
                                            }
                                            hideSelectedOptions={
                                                item?.hideSelectedOptions !== undefined ? item?.hideSelectedOptions : true
                                            }
                                        />
                                    )}
                                </Fragment>
                            );
                        })}
                </div>
            </div>
            {selectedFilterValues?.length > 0 && (
                <div className="d-flex flex-wrap mb-3">
                    <label className="text-12 fw-500 px-3 py-1 primary-color cursor-pointer" onClick={() => onClearAllFilters()}>
                        Clear all
                    </label>
                    {selectedFilterValues?.map((item, index) => {
                        if (allfiltersOptions?.find((single) => single?.value == item?.value)) {
                            return (
                                <Chip
                                    key={index}
                                    label={item?.fieldType ? `${item?.fieldType} - ${item?.label}` : item?.label}
                                    chipId={item?.value}
                                    onRemoveSelectedTenant={(id) => onRemoveSelectedFilter(id, item?.fieldType)}
                                />
                            );
                        }
                    })}
                </div>
            )}
            {isShowSelectedProductData && Array.isArray(selectedProducts) && selectedProducts?.length > 0 && (
                <DataTable
                    theme={theme}
                    className={`table selected-filter-product ${isShowCheckboxInTable ? "row-with-space" : ""} ${tableClassName}`}
                    customStyles={customStyle}
                    selectableRows={false}
                    pagination={false}
                    columns={filterProductColumn}
                    data={selectedProducts}
                    noTableHead
                    progressPending={progressPending}
                    progressComponent={skeletonModal(selectedProducts?.length)}
                />
            )}
        </div>
    );
};

export default SubHeaderComponent;
